import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Pagination,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search,
  History,
  LocationOn,
  CheckCircle,
  Person,
  Visibility,
  ContentCopy,
  Warning,
  Cancel,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { formatDateForDisplay } from '../utils/dateUtils';

interface ServiceHistoryEntry {
  _id: string;
  serviceRequestId: string | {
    _id: string;
    serviceType?: {
      name?: string;
      category?: string;
    };
    address?: {
      street?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
    frequency?: string;
  };
  customerId: string | {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  serviceType?: {
    name?: string;
    category?: string;
  };
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  scheduledDate: string;
  completedDate: string;
  serviceNotes?: string;
  weatherConditions?: string;
  crewMembers?: string[];
  duration?: number;
  status: 'completed' | 'cancelled' | 'rescheduled';
  paymentStatus?: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded' | 'skip' | null;
  paymentFailureReason?: string | null;
  paymentRetryAttempts?: number;
}

const DEFAULT_LIMIT = 25;
const SEARCH_LIMIT = 50;
const ITEMS_PER_PAGE = 20;

interface SearchOpts {
  q?: string;
  dateFrom?: string;
  dateTo?: string;
}

interface CancellationEntry {
  _id: string;
  status: 'cancelled' | 'pending_cancellation';
  cancellationDate?: string;
  cancellationReason?: string;
  cancellationType?: string;
  customerId?: { firstName?: string; lastName?: string; email?: string };
  serviceType?: { name?: string; category?: string };
  address?: { street?: string; city?: string; state?: string; zipCode?: string };
}

const ProviderServiceHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [serviceHistory, setServiceHistory] = useState<ServiceHistoryEntry[]>([]);
  const [cancellations, setCancellations] = useState<CancellationEntry[]>([]);
  const [loadingCancellations, setLoadingCancellations] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [lastQuery, setLastQuery] = useState<string | null>(null);
  const [lastDateFrom, setLastDateFrom] = useState('');
  const [lastDateTo, setLastDateTo] = useState('');

  const hasActiveSearch = lastQuery != null || lastDateFrom || lastDateTo;

  const fetchServiceHistory = async (opts?: SearchOpts) => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      const useSearchLimit = Boolean(opts?.q?.trim() || opts?.dateFrom || opts?.dateTo);
      params.set('limit', String(useSearchLimit ? SEARCH_LIMIT : DEFAULT_LIMIT));
      if (opts?.q?.trim()) params.set('q', opts.q.trim());
      if (opts?.dateFrom) params.set('dateFrom', opts.dateFrom);
      if (opts?.dateTo) params.set('dateTo', opts.dateTo);
      const url = `/service-history/provider/customers?${params.toString()}`;
      const response = await apiService.get<ServiceHistoryEntry[]>(url);

      if (response.success && response.data) {
        const historyData: ServiceHistoryEntry[] = Array.isArray(response.data) ? response.data : [];
        setServiceHistory(historyData);
      } else {
        setError('Failed to load service history');
        setServiceHistory([]);
      }
    } catch (err: any) {
      console.error('Error fetching service history:', err);
      setError(err?.message || 'Failed to load service history');
      setServiceHistory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceHistory();
  }, []);

  const fetchCancellations = async () => {
    try {
      setLoadingCancellations(true);
      setError(null);
      const response = await apiService.get<CancellationEntry[]>('/service-history/provider/cancellations?limit=100');
      if (response.success && response.data) {
        setCancellations(Array.isArray(response.data) ? response.data : []);
      } else {
        setCancellations([]);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load cancellations');
      setCancellations([]);
    } finally {
      setLoadingCancellations(false);
    }
  };

  useEffect(() => {
    if (tab === 1) fetchCancellations();
  }, [tab]);

  const handleSearch = () => {
    const q = searchTerm.trim() || undefined;
    const from = dateFrom || undefined;
    const to = dateTo || undefined;
    setLastQuery(q ?? null);
    setLastDateFrom(dateFrom);
    setLastDateTo(dateTo);
    setPage(1);
    fetchServiceHistory({ q, dateFrom: from, dateTo: to });
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
    setLastQuery(null);
    setLastDateFrom('');
    setLastDateTo('');
    setPage(1);
    fetchServiceHistory();
  };

  const getCustomerName = (customer: string | { firstName?: string; lastName?: string; email?: string }): string => {
    if (typeof customer === 'string') return 'Customer';
    if (customer.firstName || customer.lastName) {
      return `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
    }
    return customer.email || 'Customer';
  };

  const getServiceTypeName = (entry: ServiceHistoryEntry): string => {
    if (entry.serviceType?.name) {
      return entry.serviceType.name;
    }
    if (typeof entry.serviceRequestId === 'object' && entry.serviceRequestId.serviceType?.name) {
      return entry.serviceRequestId.serviceType.name;
    }
    return 'Service';
  };

  const getAddress = (entry: ServiceHistoryEntry): string => {
    if (entry.address) {
      const addr = entry.address;
      return `${addr.street || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.zipCode || ''}`.trim();
    }
    if (typeof entry.serviceRequestId === 'object' && entry.serviceRequestId.address) {
      const addr = entry.serviceRequestId.address;
      return `${addr.street || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.zipCode || ''}`.trim();
    }
    return 'Address not available';
  };

  const getServiceRequestId = (entry: ServiceHistoryEntry): string => {
    if (typeof entry.serviceRequestId === 'string') return String(entry.serviceRequestId);
    if (typeof entry.serviceRequestId === 'object' && entry.serviceRequestId != null) {
      const id = (entry.serviceRequestId as any)._id ?? entry.serviceRequestId;
      if (id != null) return String(id);
    }
    return String(entry._id);
  };

  const handleRowClick = (entry: ServiceHistoryEntry) => {
    const jobId = getServiceRequestId(entry);
    const params = new URLSearchParams();
    if (entry._id) params.set('historyId', String(entry._id));
    if (entry.completedDate) params.set('completedDate', String(entry.completedDate));
    const qs = params.toString();
    navigate(qs ? `/job/${jobId}?${qs}` : `/job/${jobId}`);
  };

  const handleCopyId = (e: React.MouseEvent, id: string) => {
    e.stopPropagation(); // Prevent row click
    navigator.clipboard.writeText(id);
    // You could add a toast notification here if desired
  };

  const paginatedHistory = serviceHistory.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );
  const totalPages = Math.max(1, Math.ceil(serviceHistory.length / ITEMS_PER_PAGE));

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const getCancellationCustomerName = (c: CancellationEntry): string => {
    const cust = c.customerId;
    if (!cust) return 'Customer';
    if (cust.firstName || cust.lastName) return `${cust.firstName || ''} ${cust.lastName || ''}`.trim();
    return cust.email || 'Customer';
  };

  const getCancellationAddress = (c: CancellationEntry): string => {
    const a = c.address;
    if (!a) return '—';
    const parts = [a.street, a.city, a.state, a.zipCode].filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <History sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Service History / Cancellations
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          {tab === 0
            ? 'View your recent completed services. Search by customer, address, zip, phone, service type, or date range.'
            : 'View services cancelled by customers or pending cancellation (awaiting admin confirmation).'}
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => { setTab(v); setError(null); }} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Service History" icon={<History />} iconPosition="start" />
        <Tab label="Cancellations" icon={<Cancel />} iconPosition="start" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {tab === 1 ? (
        <>
          {loadingCancellations ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : cancellations.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No cancellations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cancelled and pending-cancellation services will appear here.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Customer</strong></TableCell>
                    <TableCell><strong>Service</strong></TableCell>
                    <TableCell><strong>Address</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Cancellation date</strong></TableCell>
                    <TableCell><strong>Reason</strong></TableCell>
                    <TableCell><strong>Details</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cancellations.map((c) => (
                    <TableRow
                      key={c._id}
                      hover
                      onClick={() => navigate(`/job/${c._id}`)}
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                      <TableCell>{getCancellationCustomerName(c)}</TableCell>
                      <TableCell>{c.serviceType?.name || c.serviceType?.category || 'Service'}</TableCell>
                      <TableCell>{getCancellationAddress(c)}</TableCell>
                      <TableCell>
                        <Chip
                          label={c.status === 'pending_cancellation' ? 'Pending Cancellation' : 'Cancelled'}
                          size="small"
                          color={c.status === 'pending_cancellation' ? 'warning' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {c.cancellationDate ? formatDateForDisplay(c.cancellationDate) : '—'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap title={c.cancellationReason || ''}>
                          {c.cancellationReason || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/job/${c._id}`); }} title="View details">
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : (
        <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {hasActiveSearch
              ? (() => {
                  const parts: string[] = [];
                  if (lastQuery != null) parts.push(`"${lastQuery}"`);
                  if (lastDateFrom || lastDateTo) {
                    parts.push(lastDateFrom && lastDateTo
                      ? `${lastDateFrom} – ${lastDateTo}`
                      : lastDateFrom ? `from ${lastDateFrom}` : `through ${lastDateTo}`);
                  }
                  return `Search results ${parts.length ? `(${parts.join(', ')}) ` : ''}— up to ${SEARCH_LIMIT} services`;
                })()
              : `Showing your ${DEFAULT_LIMIT} most recent services. Use search or date filters to find older jobs.`}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Customer, address, zip, phone, service type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="From date"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: dateTo || undefined }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="To date"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: dateFrom || undefined }}
              />
            </Grid>
            <Grid item xs={12} md={3} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={handleSearch} disabled={loading}>
                Search
              </Button>
              <Button variant="outlined" onClick={handleClearSearch} disabled={loading}>
                Clear / Show recent {DEFAULT_LIMIT}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {paginatedHistory.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {hasActiveSearch ? 'No services found matching your search' : 'No service history yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasActiveSearch
              ? 'Try a different search or date range (customer, address, zip, phone, service type) or clear to see recent services.'
              : 'Completed services will appear here once you start completing jobs.'}
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Job ID</strong></TableCell>
                  <TableCell><strong>Customer</strong></TableCell>
                  <TableCell><strong>Service Type</strong></TableCell>
                  <TableCell><strong>Address</strong></TableCell>
                  <TableCell><strong>Job Status</strong></TableCell>
                  <TableCell><strong>Payment Status</strong></TableCell>
                  <TableCell><strong>Details</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedHistory.map((entry) => {
                  const jobId = getServiceRequestId(entry);
                  return (
                  <TableRow 
                    key={entry._id} 
                    hover 
                    onClick={() => handleRowClick(entry)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {formatDateForDisplay(entry.completedDate)}
                        </Typography>
                        {entry.scheduledDate && (
                          <Typography variant="caption" color="text.secondary">
                            Scheduled: {formatDateForDisplay(entry.scheduledDate)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            color: 'primary.main',
                            fontWeight: 'medium'
                          }}
                        >
                          {jobId.substring(0, 8)}...
                        </Typography>
                        <Tooltip title="Copy Job ID">
                          <IconButton
                            size="small"
                            onClick={(e) => handleCopyId(e, jobId)}
                            sx={{ 
                              padding: '2px',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              }
                            }}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Person fontSize="small" color="action" />
                        <Typography variant="body2">
                          {getCustomerName(entry.customerId)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getServiceTypeName(entry)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationOn fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ maxWidth: 250 }}>
                          {getAddress(entry)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<CheckCircle />}
                        label={entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                        color={entry.status === 'completed' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {entry.paymentStatus ? (
                        (() => {
                          const label = entry.paymentStatus.charAt(0).toUpperCase() + entry.paymentStatus.slice(1);
                          const chip = (
                            <Chip
                              icon={entry.paymentStatus === 'failed' ? <Warning /> : undefined}
                              label={label}
                              color={
                                entry.paymentStatus === 'completed' || entry.paymentStatus === 'skip'
                                  ? 'success'
                                  : entry.paymentStatus === 'failed'
                                    ? 'error'
                                    : 'default'
                              }
                              size="small"
                              variant={entry.paymentStatus === 'failed' ? 'outlined' : 'filled'}
                            />
                          );
                          return entry.paymentStatus === 'failed' && entry.paymentFailureReason ? (
                            <Tooltip title={entry.paymentFailureReason}>
                              <span>{chip}</span>
                            </Tooltip>
                          ) : (
                            chip
                          );
                        })()
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View Job Details">
                        <IconButton
                          size="small"
                          onClick={() => handleRowClick(entry)}
                          color="primary"
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
        </>
      )}
    </Container>
  );
};

export default ProviderServiceHistoryPage;
